import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";
import {ChatContainer, MainContainer, Message, MessageInput, MessageList, TypingIndicator} from "@chatscope/chat-ui-kit-react";
import React, {useCallback, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import WordModalWindow from './WordModalWindow.js'
import AnalizeModalWindow from './AnalizeModalWindow.js'
import {api} from './Api.js'
import WritingRoles from "./WritingRoles.js";
import PreflightLoader, {parseISODuration, usePreflightTimer} from "./PreflightLoader";
import {config} from "./Constants";
import styles from './css/Practice.module.css';

const Conversation = ({showNotification}) => {
    const [showWordModal, setShowWordModal] = useState(false);
    const [wordModalData, setWordModalData] = useState(null);
    const [showAnalizeModal, setShowAnalizeModal] = useState(false);
    const [analizeModalData, setAnalizeModalData] = useState(null);
    const [role, setRole] = useState(null);
    const [messages, setMessages] = useState(null);
    const [waiting, setWaiting] = useState(false);
    const [typing, setTyping] = useState(false);
    const [inputText, setInputText] = useState("");
    const [expectedWaitTime, setExpectedWaitTime] = useState(0);
    const timeLeft = usePreflightTimer(expectedWaitTime, typing);

    const navigate = useNavigate();

    const calcUserIndex = useCallback((messages) => {
        if (messages.length === 0) {
            return 0;
        }

        const userIndexes = messages.map(m => m.userIndex);
        const maxIndex = Math.max(...userIndexes);

        return maxIndex + 1;
    }, []);

    const handleSaveMessage = useCallback(async (group) => {
        const response = await api.saveMessages(group, navigate, showNotification);
        if (api.isValidResponse(response)) {
            setMessages(response.data);
        }
        setTyping(false);
    }, [navigate, showNotification]);

    const callSendMessage = useCallback(async (group) => {
        if (config.enablePreflight) {
            const waitRes = await api.fetchWaitTime('/writing/chat', navigate, showNotification);
            if (api.isValidResponse(waitRes)) {
                setExpectedWaitTime(parseISODuration(waitRes.data.expectedWaitTime));
            }
        }
        const response = await api.sendMessage(group, navigate, showNotification);
        if (api.isValidResponse(response)) {
            handleSaveMessage(response.data);
            setExpectedWaitTime(0);
        } else {
            setExpectedWaitTime(0);
        }
    }, [handleSaveMessage, navigate, showNotification]);

    const sendMessage = useCallback((innerHtml, textContent, innerText) => {
        if (!innerText || innerText.trim().length === 0) {
            return;
        }

        const newUserIndex = calcUserIndex(messages.messages);
        const newOutgoingMessage = {
            type: 'OUTGOING',
            text: innerText,
            userIndex: newUserIndex,
            rating: 5
        };

        const updatedMessages = {
            ...messages,
            messages: [...messages.messages, newOutgoingMessage]
        };

        setMessages(updatedMessages);
        setTyping(true);

        callSendMessage(updatedMessages);
    }, [messages, calcUserIndex, callSendMessage]);

    const callGetUserMessages = useCallback(async (roleId) => {
        const response = await api.getUserMessages(roleId, false, navigate, showNotification);
        if (api.isValidResponse(response)) {
            setWaiting(false);
            setMessages(response.data);
            if (response.data.messages.length === 0) {
                setTyping(true);
                callSendMessage(response.data);
            }
        }
    }, [callSendMessage, navigate, showNotification]);

    const renderInteractiveText = useCallback((text) => {
        const stopWords = new Set([
            "the", "and", "but", "for", "are", "was", "were", "been", "being", "this", "that", "these", "those"
        ]);

        return text
            .split(/(\s+|[.,!?;:]|\b___+\b)/)
            .map((segment, index) => {
                const isWord = /^[a-zA-Zа-яА-ЯёЁčćžšđČĆŽŠĐ]{3,}$/.test(segment);
                const isStopWord = stopWords.has(segment.toLowerCase());

                if (isWord && !isStopWord) {
                    return (
                        <button
                            key={index}
                            className={styles.interactiveWord}
                            onClick={() => {
                                setWordModalData({text, wordEn: segment});
                                setShowWordModal(true);
                            }}
                            type="button"
                        >
                            {segment}
                        </button>
                    );
                }

                return <span key={index}>{segment}</span>;
            });
    }, []);

    const handlePaste = useCallback((evt) => {
        evt.preventDefault();
        const text = evt.clipboardData.getData("text/plain");
        document.execCommand("insertHTML", false, text);
    }, []);

    const setRoleCallback = useCallback((r) => {
        setWaiting(true);
        setRole(r);
        callGetUserMessages(r.roleId);
    }, [callGetUserMessages]);

    const returnToRoleSelection = useCallback(() => {
        if (typing) {
            return;
        }

        setRole(null);
        setMessages(null);
        setInputText("");
        setExpectedWaitTime(0);
        setShowAnalizeModal(false);
        setShowWordModal(false);
    }, [typing]);

    const onCloseCallback = useCallback((index, comment, rating) => {
        const messageIndex = messages.messages.findIndex(m => m.userIndex === index);
        if (messageIndex !== -1 && messages.messages[messageIndex].comment === null) {
            const updatedMessagesList = [...messages.messages];
            updatedMessagesList[messageIndex] = {...updatedMessagesList[messageIndex], comment, rating: rating || 5};

            const updatedGroup = {...messages, messages: updatedMessagesList};
            handleSaveMessage(updatedGroup);
        }
        setShowAnalizeModal(false);
    }, [messages, handleSaveMessage]);

    if (waiting) {
        return (
            <div className="common-preloader-form">
                <PreflightLoader expectedWaitTime={expectedWaitTime}/>
            </div>
        )
    }

    if (role === null) {
        return (
            <WritingRoles showNotification={showNotification} type={"WRITING"} setRole={setRoleCallback}/>
        )
    }

    return (
        <main className={styles.page}>
            {showWordModal && (
                <div className={styles.modalOverlay}>
                    <WordModalWindow
                        wordEn={wordModalData.wordEn}
                        text={wordModalData.text}
                        onClose={() => setShowWordModal(false)}
                        showNotification={showNotification}
                        navigate={navigate}
                    />
                </div>
            )}

            {showAnalizeModal && (
                <div className={styles.modalOverlay}>
                    <AnalizeModalWindow
                        speaking={false}
                        index={analizeModalData.index}
                        text={analizeModalData.text}
                        comment={analizeModalData.comment}
                        onClose={onCloseCallback}
                        navigate={navigate}
                    />
                </div>
            )}

            <div className={styles.practiceCard}>
                <header className={styles.practiceHeader}>
                    <button className={`btn-flat white-text ${styles.headerBack}`} onClick={returnToRoleSelection} disabled={typing}
                            type="button" aria-label="Back to scenarios">
                                <i className="material-icons">arrow_back</i>
                            </button>
                    <div className={styles.headerTitle}>
                        <strong>{role?.summary}</strong>
                        <span>Free conversation · tap a highlighted word for help</span>
                    </div>
                </header>

                <div className={styles.chatArea}>
                            <MainContainer style={{height: '100%'}}>
                                <ChatContainer>
                                    <MessageList
                                        scrollBehavior="smooth"
                                        typingIndicator={typing ? (
                                            <div style={{width: '100%'}}>
                                                <TypingIndicator
                                                    content={expectedWaitTime > 0 ? `AI is thinking... ${timeLeft.toFixed(1)}s` : "AI is thinking..."}/>
                                                {expectedWaitTime > 0 &&
                                                    <PreflightLoader expectedWaitTime={expectedWaitTime}
                                                                     compact={true} hideText={true}/>}
                                            </div>
                                        ) : null}
                                    >
                                        {messages != null && messages.messages.map((message, idx) => (
                                            <Message
                                                key={idx}
                                                model={{
                                                    type: "custom",
                                                    direction: message.type === 'INCOMING' ? "incoming" : "outgoing",
                                                    position: "single"
                                                }}
                                            >
                                                <Message.CustomContent>
                                                    <div className={styles.messageText}>
                                                        {renderInteractiveText(message.text)}
                                                    </div>
                                                    {message.type === 'OUTGOING' && (
                                                        <div className="right-align">
                                                            <button
                                                                className={`btn-flat btn-small blue-text ${styles.messageAction}`}
                                                                onClick={() => {
                                                                    setAnalizeModalData({
                                                                        index: message.userIndex,
                                                                        text: message.text,
                                                                        comment: message.comment
                                                                    });
                                                                    setShowAnalizeModal(true);
                                                                }}
                                                            >
                                                                <i className="material-icons">analytics</i>
                                                                Review writing
                                                            </button>
                                                        </div>
                                                    )}
                                                </Message.CustomContent>
                                            </Message>
                                        ))}
                                    </MessageList>
                                    <MessageInput
                                        placeholder={typing ? "Your tutor is responding…" : "Write your message in English…"}
                                        value={inputText}
                                        onChange={val => setInputText(val)}
                                        onSend={(innerHtml, textContent, innerText) => {
                                            sendMessage(innerHtml, textContent, innerText);
                                            setInputText("");
                                        }}
                                        onPaste={handlePaste}
                                        attachButton={false}
                                        disabled={typing}
                                        sendOnReturnDisabled={typing}
                                    />
                                </ChatContainer>
                            </MainContainer>
                        </div>
            </div>
        </main>
    );
};

export default Conversation;
